import { useRef, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChannelsService } from '../../../../services/channels.service';
import { AlertService } from '../../../../services/alert.service';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import Switch from '../../../../components/forms/switch.component';
import Checkbox from '../../../../components/forms/checkbox.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import useInputValidation from '../../../../hooks/use-input-validation.hook';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import Icon from '../../../../components/forms/icon.component';

interface ProviderSettings {
  required: boolean;
  type: string;
}

interface ProviderOption {
  value: string;
  label: string;
  type: string;
  settings: { [key: string]: ProviderSettings };
}

interface OutputsModalProps {
  readonly providers: ProviderOption[];
  readonly show: boolean;
  readonly onRequestClose: (refresh: boolean) => void;
  readonly payload: any;
}

function OutputsModal({ payload, show, providers, onRequestClose }: OutputsModalProps) {
  const { t } = useTranslation();
  const { numericRegex, handleNumericInput } = useInputValidation();
  const [selectedProvider, setSelectedProvider] = useState<ProviderOption | null>(null);
  const [dynamicSettings, setDynamicSettings] = useState<{ [key: string]: any }>({});
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);

  const defaultValues = {
    _id: '',
    name: '',
    provider: '',
    settings: {},
    telcel: false,
    movistar: false,
    att: false,
    other: false,
    type: [],
    is_international: false,
    carriers: [] as string[]
  };

  const {
    register,
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const telcelShow = watch('telcel');
  const movistarShow = watch('movistar');
  const attShow = watch('att');
  const altanShow = watch('other');
  const isAnySwitchActive = telcelShow || movistarShow || attShow || altanShow;

  const handleProviderChange = (selectedOption: ProviderOption | null) => {
    setSelectedProvider(selectedOption);

    if (selectedOption) {
      const newSettings: { [key: string]: any } = {};
      Object.keys(selectedOption.settings).forEach((key) => {
        newSettings[key] = '';
      });
      setDynamicSettings(newSettings);
    }
  };

  const registerOutputSMS = async (data: any, buttonId: string) => {
    const { name, provider, telcel, movistar, att, other, is_international } = data;
    const settingsData: { [key: string]: any } = {};
    Object.keys(selectedProvider?.settings || {}).forEach((keyParam) => {
      settingsData[keyParam] = data[keyParam];
    });

    const updatedCarriers = is_international
      ? ['other']
      : [
          ...(telcel ? ['telcel'] : []),
          ...(movistar ? ['movistar'] : []),
          ...(att ? ['att'] : []),
          ...(other ? ['other'] : [])
        ];

    const dataSend: { [key: string]: any } = {
      name,
      provider,
      settings: settingsData,
      is_international,
      operators: updatedCarriers
    };
    if (payload != null) {
      dataSend._id = payload._id;
    }
    const messageQuestion = payload
      ? '¿Deseas editar esta salida?'
      : '¿Deseas registrar esta salida?';

    const isConfirmed = await AlertService.question(
      t('general.atention'),
      messageQuestion + '<br><br>',
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      if (payload != null) {
        await ChannelsService.updateOutputSMS(dataSend);
        AlertService.toastSuccess(t('channels.rcs.modal-C-succesEdit2'));
      } else {
        await ChannelsService.addOutputSMS(dataSend);
        AlertService.toastSuccess(t('channels.rcs.modal-C-succes2'));
      }
      LogData(data, buttonId, window.location.href);
      onRequestClose(true);
      reset(defaultValues);
      setDynamicSettings({});
      setSelectedProvider(null);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
    setDynamicSettings({});
    setSelectedProvider(null);
  };

  useEffect(() => {
    if (payload) {
      const newSettings: { [key: string]: any } = {};
      if (payload.provider && providers.length) {
        const selectedProvider = providers.find(
          (provider) => provider.value === payload.provider?._id
        );
        if (selectedProvider) {
          Object.keys(selectedProvider.settings).forEach((key) => {
            newSettings[key] = payload.settings?.[key] || '';
          });
          setSelectedProvider(selectedProvider);
          setDynamicSettings(newSettings);
        }
      }
      reset({
        _id: payload._id || defaultValues._id,
        name: payload.name || defaultValues.name,
        provider: payload.provider?._id || defaultValues.provider,
        type: payload.type || defaultValues.type,
        telcel: payload.operators?.includes('telcel') || defaultValues.telcel,
        movistar: payload.operators?.includes('movistar') || defaultValues.movistar,
        att: payload.operators?.includes('att') || defaultValues.att,
        other: payload.operators?.includes('other') || defaultValues.other,
        is_international: payload.is_international ?? defaultValues.is_international,
        carriers: payload.operators || defaultValues.carriers,
        ...newSettings
      });
    }
  }, [payload]);

  useEffect(() => {}, []);
  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-xl">
        <div className="modal-content rounded">
          <div className="modal-header justify-content-end border-0 pb-0">
            <button
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
              type="button"
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = watch('_id') ? 'editOutput-0273847673' : 'addOutput-1909090923';
              handleSubmit((data) => registerOutputSMS(data, buttonID))(e);
            }}
          >
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {watch('_id') ? (
                    <>
                      {t('channels.rcs.modal-C-titleEdit')}
                      <span className="link-primary fw-bold ms-2">{payload?.name}</span>
                    </>
                  ) : (
                    <> {t('channels.rcs.modal-C-title')}</>
                  )}
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row mt-10">
                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <Title
                    title={'Nombre y proveedor'}
                    subtitle={t('channels.rcs.modal-C-seccion1-Subtitle')}
                    icon="ki-questionnaire-tablet"
                    type="info"
                  />
                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.rcs.tc-name')}
                      requiredIndicator="required"
                      placeholder={t('channels.rcs.modal-C-seccion1-placeholder')}
                      errors={errors.name}
                      {...register('name', { required: true })}
                    />
                  </div>
                  <div className="fv-row mb-15">
                    <label className="form-label required">{t('channels.rcs.agent-tc-2')}</label>
                    <Controller
                      control={control}
                      name="provider"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => {
                        return (
                          <>
                            <Select
                              options={providers}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                              className={
                                error ? 'form-select-custom is-invalid' : 'form-select-custom'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={t('channels.rcs.modal-C-provider-placeholder')}
                              onChange={(selectedOption) => {
                                if (selectedOption) {
                                  onChange(selectedOption.value);
                                  handleProviderChange(selectedOption);
                                }
                              }}
                              value={providers.find((option) => option.value === value) || null}
                            />
                            <InvalidFeedback error={error} />
                          </>
                        );
                      }}
                    />
                  </div>
                  {selectedProvider && (
                    <>
                      <Title
                        title={'Configuración de proveedor'}
                        subtitle={t('channels.rcs.modal-C-settings-subtitle')}
                        required={true}
                        icon="ki-setting-2"
                        type="danger"
                      />
                      {Object.entries(selectedProvider.settings).map(([keyParam, settingsObj]) => (
                        <div key={keyParam} className="fv-row mb-7">
                          <Input
                            key={keyParam}
                            label={keyParam}
                            requiredIndicator={settingsObj.required ? 'required' : ''}
                            optional={!settingsObj.required}
                            placeholder={t('channels.rcs.modal-C-settings-add', {
                              setting: keyParam
                            })}
                            onKeyDown={
                              settingsObj.type === 'number' ? handleNumericInput : undefined
                            }
                            {...register(keyParam as any, {
                              required: settingsObj.required || false,
                              pattern:
                                settingsObj.type === 'number'
                                  ? {
                                      value: numericRegex,
                                      message: 'Debe ingresar solo números'
                                    }
                                  : undefined,
                              value: dynamicSettings[keyParam] || ''
                            })}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <div className="d-flex flex-stack mb-20">
                    <div className="d-flex">
                      <Title
                        title={t('channels.rcs.international')}
                        subtitle={
                          'Al activar solo podrás hacer envíos por otra salida alternativa.'
                        }
                        required={true}
                        icon="ki-setting-2"
                        type="danger"
                      />
                    </div>
                    <div className="d-flex justify-content-end m">
                      <div className="form-check form-check-solid form-check-custom form-switch">
                        <Switch {...register('is_international')} />
                      </div>
                    </div>
                  </div>
                  {watch('is_international') !== true && (
                    <>
                      <div className="row mb-7 ms-20">
                        <div className="col-md-6">
                          <div className="d-flex flex-stack align-items-center mb-5">
                            <div className="d-flex align-items-center me-2">
                              <div className="d-flex flex-column me-6">
                                <div className="fw-bold fs-6 theme-light-show">
                                  <img
                                    src="/assets/media/operators/telcel.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <div className="fw-bold fs-6 theme-dark-show">
                                  <img
                                    src="/assets/media/operators/telcel-dark.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <span className="fs-7 text-muted"></span>
                              </div>
                              <div className="form-check form-check-custom form-check-solid ms-auto">
                                <Checkbox {...register('telcel')} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex flex-stack align-items-center">
                            <div className="d-flex align-items-center me-2">
                              <div className="d-flex flex-column me-6">
                                <div className="fw-bold fs-6 theme-light-show">
                                  <img
                                    src="/assets/media/operators/movistar.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <div className="fw-bold fs-6 theme-dark-show">
                                  <img
                                    src="/assets/media/operators/movistar-dark.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <span className="fs-7 text-muted"></span>
                              </div>
                              <div className="form-check form-check-custom form-check-solid ms-auto">
                                <Checkbox {...register('movistar')} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row ms-20 mb-20">
                        <div className="col-md-6">
                          <div className="d-flex flex-stack align-items-center">
                            <div className="d-flex align-items-center me-2">
                              <div className="d-flex flex-column me-6">
                                <div className="fw-bold fs-6 theme-light-show">
                                  <img
                                    src="/assets/media/operators/att.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <div className="fw-bold fs-6 theme-dark-show">
                                  <img
                                    src="/assets/media/operators/att-dark.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <span className="fs-7 text-muted"></span>
                              </div>
                              <div className="form-check form-check-custom form-check-solid ms-auto">
                                <Checkbox {...register('att')} />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex flex-stack align-items-center">
                            <div className="d-flex align-items-center me-2">
                              <div className="d-flex flex-column me-6">
                                <div className="fw-bold fs-6 theme-light-show">
                                  <img
                                    src="/assets/media/operators/altan.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <div className="fw-bold fs-6 theme-dark-show">
                                  <img
                                    src="/assets/media/operators/altan-dark.png"
                                    alt="imageMask"
                                    width="70px"
                                  />
                                </div>
                                <span className="fs-7 text-muted"></span>
                              </div>
                              <div className="form-check form-check-custom form-check-solid ms-auto">
                                <Checkbox {...register('other')} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {!watch('is_international') && !isAnySwitchActive && (
                    <div className="alert alert-primary d-flex align-items-center p-2 animated-Alert">
                      <Icon icon="ki-abstract-12" size="fs-2hx" type="primary" className="me-4" />
                      <div className="d-flex flex-column">
                        <h4 className="mb-1">{t('errors.title-text-alert-atencion')}</h4>
                        <span>
                          Debes seleccionar al menos un operador si tu salida no es internacional.
                          Si es internacional, los envios se realizarán automáticamente a través de
                          una salida alternativa.
                        </span>
                      </div>
                    </div>
                  )}
                  {watch('is_international') && (
                    <div className="alert alert-primary d-flex align-items-center p-2 animated-Alert">
                      <Icon icon="ki-abstract-12" size="fs-2hx" type="primary" className="me-4" />
                      <div className="d-flex flex-column">
                        <h4 className="mb-1">{t('errors.title-text-alert-atencion')}</h4>
                        <span>
                          Los envíos se realizarán automáticamente a través de una salida
                          alternativa.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || (!watch('is_international') && !isAnySwitchActive)}
                  isLoading={isSubmitting}
                >
                  {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OutputsModal;
